<template>
    <div class="relative">
        <input
            class="text-left block w-full bg-white dark:bg-slate-800 rounded border py-1.5 text-gray-900 dark:text-gray-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm transition-colors focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
            :class="[
                error
                    ? 'border-red-500 focus:border-red-500 hover:border-red-500'
                    : 'border-slate-200 dark:border-slate-700 focus:border-slate-800 dark:focus:border-slate-300 hover:border-slate-800 dark:hover:border-slate-300',
                inputClass,
                'pr-8',
            ]"
            :id="id"
            :placeholder="placeholder"
            ref="input"
            v-model="textInput"
            type="text"
            @keydown.enter.prevent="chooseActivePlace"
            @keydown.down="down"
            @keydown.up="up"
            @input="search"
            @focus="$emit('onFocus')"
            autocomplete="off"
            autofocus
            v-show="!hasChosenPlace"
        />
        <div
            v-if="hasChosenPlace"
            class="relative w-full h-10 bg-white dark:bg-slate-800 rounded border py-1.5 pl-4 pr-10 text-slate-800 dark:text-slate-100 text-sm flex items-center"
            :class="[error ? 'border-red-500' : 'border-slate-200 dark:border-slate-700', inputClass]"
        >
            <LoadingState :loading="isLoadingPlace" size="small" no-background />
            <span class="truncate">{{ chosenPlace }}</span>
            <button
                v-if="!hideClear"
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                @click="clearPlace()"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                    ></path>
                </svg>
            </button>
        </div>
        <transition name="fade">
            <ul
                class="block w-full max-h-64 overflow-y-auto bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 shadow dark:shadow-slate-900/50 absolute top-10 left-0 z-30 p-1"
                v-if="places.length > 0"
            >
                <li
                    v-for="place in places"
                    :key="place"
                    @click="selectPlace(place)"
                    class="rounded px-4 py-1.5 text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                    :class="{
                        'bg-white dark:bg-slate-800': !place.active,
                        'bg-slate-200 dark:bg-slate-700': place.active,
                    }"
                >
                    {{ place.formattedAddress }}
                </li>
            </ul>
        </transition>
    </div>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import { useDebounce } from '@/composables/useDebounce';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';

const emit = defineEmits(['placeChanged', 'placeCleared', 'onFocus']);

defineOptions({ inheritAttrs: false });

const props = defineProps({
    types: {
        type: Array,
        default: () => [],
    },
    placeholder: {
        type: String,
        default: '',
    },
    id: {
        type: String,
        default: '',
    },
    currentPlace: {
        type: Object,
        default: null,
    },
    error: {},
    inputClass: {
        type: String,
        default: '',
    },
    hideClear: {
        type: Boolean,
        default: false,
    },
});

const input = ref(null);

const places = ref([]);
const activeIndex = ref(-1);
const searchInput = ref('');
const isLoadingPlace = ref(false);

const textInput = computed({
    get() {
        return props.currentPlace && props.currentPlace.formattedAddress
            ? props.currentPlace.formattedAddress
            : '';
    },
    set(value) {
        searchInput.value = value;
    },
});

const chosenPlace = ref(null);
const hasChosenPlace = ref(false);

watch(
    () => props.currentPlace,
    (newValue) => {
        if (newValue && newValue.formattedAddress) {
            chosenPlace.value = newValue.formattedAddress;
            hasChosenPlace.value = true;
        } else if (!newValue) {
            chosenPlace.value = null;
            textInput.value = '';
            searchInput.value = '';
            places.value = [];
            activeIndex.value = -1;
            hasChosenPlace.value = false;
        } else {
            chosenPlace.value = null;
            hasChosenPlace.value = false;
        }
    },
    { immediate: true, deep: true }
);

const { AutocompleteSessionToken, AutocompleteSuggestion, Place } =
    await google.maps.importLibrary('places');

const performSearch = async () => {
    if (searchInput.value === '') return;
    places.value = [];

    let request = {
        input: searchInput.value,
        includedPrimaryTypes: props.types,
        language: 'en-US',
    };

    const token = new AutocompleteSessionToken();

    request.sessionToken = token;

    const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    places.value = suggestions.map((s) => ({
        placeId: s.placePrediction.placeId,
        formattedAddress: s.placePrediction.text.toString(),
        active: false,
    }));
};

const { debounce } = useDebounce();
const search = debounce(performSearch, 300);

/**
 * Fetch full place details for a given place ID and emit the result.
 */
const fetchPlaceDetailsAndEmit = async (placeId, displayText) => {
    isLoadingPlace.value = true;
    chosenPlace.value = displayText;
    hasChosenPlace.value = true;
    textInput.value = displayText;
    places.value = [];
    activeIndex.value = -1;

    try {
        const place = new Place({ id: placeId });

        await place.fetchFields({
            fields: [
                'displayName',
                'formattedAddress',
                'addressComponents',
                'location',
            ],
        });

        let addressByTypes = {};
        for (var i = 0; i < place.addressComponents.length; i++) {
            let c = place.addressComponents[i];
            addressByTypes[c.types[0]] = {
                shortName: c.shortText,
                longName: c.longText,
            };
        }

        const placeData = {
            displayName: place.displayName,
            formattedAddress: place.formattedAddress,
            lat: place.location.lat(),
            lng: place.location.lng(),
            addressTypes: addressByTypes,
        };

        chosenPlace.value = place.formattedAddress;
        emit('placeChanged', placeData);
    } finally {
        isLoadingPlace.value = false;
    }
};

const selectPlace = (place) => {
    fetchPlaceDetailsAndEmit(place.placeId, place.formattedAddress);
};

const chooseActivePlace = () => {
    if (
        activeIndex.value < 0 ||
        activeIndex.value >= places.value.length ||
        places.value.length === 0
    ) {
        return;
    }

    const place = places.value[activeIndex.value];
    fetchPlaceDetailsAndEmit(place.placeId, place.formattedAddress);
};

const clearPlace = () => {
    places.value = [];
    textInput.value = '';
    chosenPlace.value = null;
    searchInput.value = '';
    activeIndex.value = -1;
    input.value.focus();
    emit('placeCleared');
};

const up = () => {
    if (activeIndex.value > 0) {
        if (activeIndex.value > 0) {
            activeIndex.value--;
        }
        resetActiveItem();
        places.value[activeIndex.value].active = true;
    }
};

const down = () => {
    if (activeIndex.value + 1 < places.value.length) {
        activeIndex.value++;
        resetActiveItem();
        places.value[activeIndex.value].active = true;
    }
};

const resetActiveItem = () => {
    places.value.forEach((item) => {
        item.active = false;
    });
};
</script>
